import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Coins, Film, ImagePlus, Loader2, X } from 'lucide-react'

import { assetsApi } from '@/api/assets'
import { feedbackApi, type CreateFeedbackBody, type FeedbackCategory } from '@/api/feedback'
import type { SilkAssetKind } from '@/api/types'
import { LoginRequiredState } from '@/features/auth/components/LoginRequiredState'
import { useAuth } from '@/hooks'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Textarea } from '@/shared/components/ui/textarea'
import { notifyApiError, toast } from '@/shared/lib/notify'
import { cn } from '@/shared/lib/utils'

const CATEGORIES: FeedbackCategory[] = ['bug', 'suggestion', 'experience', 'other']
const MAX_ATTACHMENTS = 4
const ACCEPT_MEDIA = 'image/*,video/*'

type AttachmentItem = {
  id: string
  url: string
  kind: 'image' | 'video'
  name: string
}

type Props = {
  open: boolean
  onClose: () => void
  onSubmitted?: () => void
}

function kindFromFile(file: File): SilkAssetKind | null {
  if (file.type.startsWith('image/')) return 'image'
  if (file.type.startsWith('video/')) return 'video'
  return null
}

export function FeedbackDialog({ open, onClose, onSubmitted }: Props) {
  const { t } = useTranslation()
  const { isAuthenticated } = useAuth()
  const fileRef = useRef<HTMLInputElement>(null)
  const [category, setCategory] = useState<FeedbackCategory>('bug')
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [hints, setHints] = useState<Partial<Record<FeedbackCategory, number>>>({})
  const [attachments, setAttachments] = useState<AttachmentItem[]>([])
  const [uploadingCount, setUploadingCount] = useState(0)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    void feedbackApi.rewardHints().then((res) => setHints(res.hints)).catch(() => {})
  }, [open])

  useEffect(() => {
    if (!open) {
      setCategory('bug')
      setTitle('')
      setContent('')
      setAttachments([])
      setUploadingCount(0)
    }
  }, [open])

  const selectedReward = hints[category] ?? 0
  const maxReward = useMemo(
    () => Math.max(0, ...CATEGORIES.map((c) => hints[c] ?? 0)),
    [hints],
  )

  const uploadFiles = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files)
      const remaining = MAX_ATTACHMENTS - attachments.length
      if (remaining <= 0) {
        toast.warning(t('feedback.form.maxAttachments', { n: MAX_ATTACHMENTS }))
        return
      }
      const batch = list.slice(0, remaining)
      for (const file of batch) {
        const kind = kindFromFile(file)
        if (!kind) {
          toast.warning(t('feedback.form.invalidMedia'))
          continue
        }
        setUploadingCount((n) => n + 1)
        try {
          const asset = await assetsApi.upload(file, kind)
          setAttachments((prev) => [
            ...prev,
            {
              id: `${asset.id}-${Date.now()}`,
              url: asset.url,
              kind: kind === 'video' ? 'video' : 'image',
              name: asset.filename || file.name,
            },
          ])
        } catch (err) {
          notifyApiError(err)
        } finally {
          setUploadingCount((n) => Math.max(0, n - 1))
        }
      }
    },
    [attachments.length, t],
  )

  const removeAttachment = (id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id))
  }

  const submit = useCallback(async () => {
    const trimmed = content.trim()
    if (!trimmed) {
      toast.warning(t('feedback.form.contentRequired'))
      return
    }
    if (uploadingCount > 0) {
      toast.warning(t('feedback.form.waitUpload'))
      return
    }
    setSubmitting(true)
    try {
      const body: CreateFeedbackBody = {
        category,
        title: title.trim() || undefined,
        content: trimmed,
        page_url: typeof window !== 'undefined' ? window.location.href : undefined,
        screenshot_urls: attachments.length > 0 ? attachments.map((a) => a.url) : undefined,
      }
      await feedbackApi.create(body)
      toast.success(t('feedback.form.submitSuccess'))
      onSubmitted?.()
      onClose()
    } catch (err) {
      notifyApiError(err)
    } finally {
      setSubmitting(false)
    }
  }, [attachments, category, content, onClose, onSubmitted, t, title, uploadingCount])

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
        <DialogHeader className="border-b px-5 py-4 text-left">
          <DialogTitle>{t('feedback.dialogTitle')}</DialogTitle>
          <DialogDescription>{t('feedback.dialogSubtitle')}</DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {!isAuthenticated ? (
            <LoginRequiredState contained={false} redirectTo="/account/feedback" tone="indigo" />
          ) : (
            <div className="space-y-3.5">
              <div className="flex items-start gap-2 rounded-xl border border-emerald-500/25 bg-emerald-500/10 px-3 py-2.5">
                <Coins size={16} className="mt-0.5 shrink-0 text-emerald-300" />
                <div className="min-w-0 text-xs leading-relaxed text-emerald-100/90">
                  <p className="font-medium text-emerald-200">{t('feedback.rewardBannerTitle')}</p>
                  <p className="mt-0.5 text-emerald-100/80">
                    {selectedReward > 0
                      ? t('feedback.rewardBannerSelected', { points: selectedReward })
                      : t('feedback.rewardBannerMax', { points: maxReward || '—' })}
                  </p>
                </div>
              </div>

              <div>
                <Label className="mb-1.5 text-xs text-zinc-400">{t('feedback.form.category')}</Label>
                <div className="flex flex-wrap gap-1.5">
                  {CATEGORIES.map((key) => {
                    const pts = hints[key]
                    return (
                      <Button
                        key={key}
                        type="button"
                        size="xs"
                        variant={category === key ? 'default' : 'outline'}
                        className={cn('rounded-full', category !== key && 'border-border bg-muted/30')}
                        onClick={() => setCategory(key)}
                      >
                        <span>{t(`feedback.categoriesShort.${key}`)}</span>
                        {pts != null && pts > 0 ? (
                          <span className="text-[10px] text-emerald-400/90">+{pts}</span>
                        ) : null}
                      </Button>
                    )
                  })}
                </div>
              </div>

              <div>
                <Label htmlFor="feedback-title" className="mb-1.5 text-xs text-zinc-400">
                  {t('feedback.form.title')}
                </Label>
                <Input
                  id="feedback-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={200}
                  placeholder={t('feedback.form.titlePlaceholder')}
                />
              </div>

              <div>
                <Label htmlFor="feedback-content" className="mb-1.5 text-xs text-zinc-400">
                  {t('feedback.form.content')}
                </Label>
                <Textarea
                  id="feedback-content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={4}
                  placeholder={t('feedback.form.contentPlaceholder')}
                />
              </div>

              <div>
                <div className="mb-1.5 flex items-center justify-between gap-2">
                  <Label className="text-xs text-zinc-400">{t('feedback.form.attachments')}</Label>
                  <span className="text-[10px] text-zinc-600">
                    {attachments.length}/{MAX_ATTACHMENTS}
                  </span>
                </div>
                <p className="mb-2 text-[10px] leading-relaxed text-zinc-600">{t('feedback.form.attachHint')}</p>
                <div className="flex flex-wrap gap-2">
                  {attachments.map((item) => (
                    <div
                      key={item.id}
                      className="group relative h-16 w-16 overflow-hidden rounded-lg border border-border bg-muted/30"
                    >
                      {item.kind === 'image' ? (
                        <img src={item.url} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full flex-col items-center justify-center gap-0.5 text-zinc-500">
                          <Film size={14} />
                          <span className="max-w-full truncate px-1 text-[8px]">{item.name}</span>
                        </div>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => removeAttachment(item.id)}
                        className="absolute inset-0 h-full w-full rounded-none bg-black/55 opacity-0 transition group-hover:opacity-100"
                        aria-label={t('common.delete')}
                      >
                        <X size={14} className="text-white" />
                      </Button>
                    </div>
                  ))}
                  {uploadingCount > 0 ? (
                    <div className="flex h-16 w-16 items-center justify-center rounded-lg border border-dashed border-border bg-muted/30">
                      <Loader2 size={18} className="animate-spin text-indigo-300" />
                    </div>
                  ) : null}
                  {attachments.length + uploadingCount < MAX_ATTACHMENTS ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileRef.current?.click()}
                      className="flex h-16 w-16 flex-col gap-0.5 rounded-lg border-dashed p-0 text-zinc-500 hover:border-indigo-500/40 hover:text-indigo-200"
                    >
                      <ImagePlus size={16} />
                      <span className="text-[9px]">{t('feedback.form.addMedia')}</span>
                    </Button>
                  ) : null}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept={ACCEPT_MEDIA}
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files?.length) void uploadFiles(e.target.files)
                    e.target.value = ''
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {isAuthenticated ? (
          <DialogFooter className="border-t bg-transparent px-5 py-4 sm:justify-end">
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              disabled={submitting || uploadingCount > 0}
              onClick={() => void submit()}
            >
              {submitting ? t('common.submitting') : t('feedback.form.submit')}
            </Button>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
